/** Persisted on failed AI messages via `statusShort` (survives reload). */
export const GENERATION_FAILED_STATUS = "GENERATION_FAILED";

export const GENERATION_ERROR_TEXTS = [
  "An error occurred while generating the response.",
  "Der opstod en fejl under generering af svaret.",
  "Ett fel uppstod vid generering av svaret.",
] as const;

const UNKNOWN_STREAM_ERROR = "Ukendt streamingfejl";

const STREAM_ABORT_PATTERNS = [
  /^abort/i,
  /aborted/i,
  /signal is aborted/i,
  /cancelled/i,
  /canceled/i,
] as const;

export const getGenerationErrorText = (languageCode?: string): string => {
  if (languageCode === "en") {
    return GENERATION_ERROR_TEXTS[0];
  }
  if (languageCode === "sw") {
    return GENERATION_ERROR_TEXTS[2];
  }
  return GENERATION_ERROR_TEXTS[1];
};

export const isStreamAbortError = (errorMessage: string): boolean =>
  STREAM_ABORT_PATTERNS.some((pattern) => pattern.test(errorMessage));

export const isExpiredGenerationError = (message: string): boolean => {
  const trimmed = message.trim();
  if (!trimmed) {
    return false;
  }

  return (
    /\b404\b/.test(trimmed) ||
    /not found/i.test(trimmed) ||
    /generation not found/i.test(trimmed)
  );
};

export const isNonRetryableHttpStreamError = (message: string): boolean => {
  const trimmed = message.trim();
  if (!trimmed) {
    return false;
  }

  return /\b401\b/.test(trimmed) || /\b403\b/.test(trimmed);
};

export const isTransportStreamError = (message: string): boolean => {
  if (!message || message === UNKNOWN_STREAM_ERROR) {
    return true;
  }

  if (isExpiredGenerationError(message)) {
    return false;
  }

  return (
    /^fetch /i.test(message) ||
    /network/i.test(message) ||
    /failed to fetch/i.test(message) ||
    /HTTP/i.test(message) ||
    /TypeError/i.test(message)
  );
};

/** Transport errors that are safe to resubscribe/retry (excludes auth failures). */
export const isRetryableTransportStreamError = (message: string): boolean =>
  isTransportStreamError(message) && !isNonRetryableHttpStreamError(message);

/** Map transport-layer stream errors to localized generation-failed copy. */
export const resolvePersistableStreamError = (
  errorMessage: string,
  languageCode?: string
): string => {
  const trimmed = errorMessage.trim();
  if (
    (GENERATION_ERROR_TEXTS as readonly string[]).includes(trimmed) ||
    isTransportStreamError(trimmed)
  ) {
    return getGenerationErrorText(languageCode);
  }
  return trimmed;
};

const RETRY_GENERATION_LABELS = {
  en: {
    button: "Try again",
    ariaLabel: "Try generating response again",
  },
  sw: {
    button: "Försök igen",
    ariaLabel: "Försök generera svar igen",
  },
  da: {
    button: "Prøv igen",
    ariaLabel: "Prøv at generere svar igen",
  },
} as const;

export const getRetryGenerationLabels = (language?: string) => {
  if (language === "en") {
    return RETRY_GENERATION_LABELS.en;
  }
  if (language === "sw") {
    return RETRY_GENERATION_LABELS.sw;
  }
  return RETRY_GENERATION_LABELS.da;
};

export const isGenerationFailedMessage = (message: {
  human: boolean;
  statusShort?: string;
  text?: string;
}): boolean => {
  if (message.human) {
    return false;
  }
  if (message.statusShort === GENERATION_FAILED_STATUS) {
    return true;
  }
  return GENERATION_ERROR_TEXTS.some((copy) => message.text === copy);
};
